// ProcessorManager.js
import React, { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Globe,
  Lock,
  Save,
  X,
  AlertCircle,
} from "lucide-react";
import api from "./api";
import "./ProcessorManager.css";

const ProcessorManager = () => {
  const [processors, setProcessors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingProcessor, setEditingProcessor] = useState(null);
  const [discoveryUrl, setDiscoveryUrl] = useState("");
  const [discovering, setDiscovering] = useState(false);
  const [, setDiscoveryResult] = useState(null);
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    description: "",
    scope: ["file"],
    apiEndpoint: "",
    apiConfiguration: {
      method: "POST",
      headers: {},
      timeout: 30000,
      retries: 3,
    },
    configuration: {},
    // ✅ Add discovery fields
    autoDiscovered: false,
    discoveryUrl: "",
    discoveredContract: null,
  });

  useEffect(() => {
    fetchProcessors();
  }, []);

  const handleDiscover = async () => {
    if (!discoveryUrl) return;

    setDiscovering(true);
    setError("");

    try {
      console.log("🔍 Discovery URL:", discoveryUrl); // Debug log
      console.log("🔍 Request payload:", { url: discoveryUrl }); // Debug log
      const response = await api.post("/processors/admin/discover", {
        url: discoveryUrl,
      });

      console.log("🔍 Discovery response:", response.data);
      setDiscoveryResult(response.data);
      setFormData((prev) => ({
        ...prev,
        id: response.data.contract.name.toLowerCase().replace(/\s+/g, "-"),
        name: response.data.contract.name,
        description: response.data.contract.description,
        scope: response.data.contract.scope,
        autoDiscovered: true,
        discoveryUrl: discoveryUrl,
        discoveredContract: response.data.contract,
        apiEndpoint: "",
      }));
      setShowForm(true);
    } catch (err) {
      console.error("🔍 Discovery error:", err);
      console.error("🔍 Error response:", err.response?.data);
      setError(err.response?.data?.error || "Discovery failed");
    } finally {
      setDiscovering(false);
    }
  };

  const fetchProcessors = async () => {
    try {
      const response = await api.get("/processors/admin/all");
      setProcessors(response.data);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch processors");
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      id: "",
      name: "",
      description: "",
      scope: ["file"],
      apiEndpoint: "",
      apiConfiguration: {
        method: "POST",
        headers: {},
        timeout: 30000,
        retries: 3,
      },
      configuration: {},
    });
    setEditingProcessor(null);
    setShowForm(false);
  };

  const handleEdit = (processor) => {
    setEditingProcessor(processor);
    setFormData({
      id: processor.id,
      name: processor.name,
      description: processor.description,
      scope: processor.scope,
      apiEndpoint: processor.apiEndpoint || "",
      apiConfiguration: processor.apiConfiguration || {
        method: "POST",
        headers: {},
        timeout: 30000,
        retries: 3,
      },
      configuration: processor.configuration || {},
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      if (editingProcessor) {
        await api.put(`/processors/admin/${editingProcessor.id}`, formData);
      } else {
        await api.post("/processors/admin", formData);
      }
      await fetchProcessors();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save processor");
    }
  };

  const handlePublishToggle = async (processor) => {
    try {
      await api.patch(`/processors/admin/${processor.id}`, {
        published: !processor.published,
      });
      await fetchProcessors();
    } catch (err) {
      setError("Failed to update processor publication status");
    }
  };

  const handleDelete = async (processor) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${processor.name}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await api.delete(`/processors/admin/${processor.id}`);
      await fetchProcessors();
    } catch (err) {
      setError("Failed to delete processor");
    }
  };

  const handleHeaderChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      apiConfiguration: {
        ...prev.apiConfiguration,
        headers: {
          ...prev.apiConfiguration.headers,
          [key]: value,
        },
      },
    }));
  };

  const addHeader = () => {
    const key = prompt("Enter header name:");
    if (key) {
      handleHeaderChange(key, "");
    }
  };

  const removeHeader = (key) => {
    setFormData((prev) => {
      const newHeaders = { ...prev.apiConfiguration.headers };
      delete newHeaders[key];
      return {
        ...prev,
        apiConfiguration: {
          ...prev.apiConfiguration,
          headers: newHeaders,
        },
      };
    });
  };

  if (loading) {
    return <div className="loading">Loading processors...</div>;
  }

  return (
    <div className="processor-manager">
      <div className="processor-header">
        <h2>Processor Management</h2>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          <Plus size={18} />
          Add Processor
        </button>
      </div>

      {error && (
        <div className="error-alert">
          <AlertCircle size={20} />
          {error}
        </div>
      )}
      <div className="discovery-section">
        <h3>Discover Processor</h3>
        <div className="discovery-form">
          <input
            type="url"
            value={discoveryUrl}
            onChange={(e) => setDiscoveryUrl(e.target.value)}
            placeholder="http://localhost:3001"
            className="form-input"
          />
          <button
            onClick={handleDiscover}
            disabled={discovering || !discoveryUrl}
            className="btn-primary"
          >
            {discovering ? "Discovering..." : "Discover Processor"}
          </button>
        </div>
      </div>

      {/* Processor List */}
      <div className="processor-list">
        {processors.map((processor) => (
          <div key={processor.id} className="processor-card">
            <div className="processor-info">
              <div className="processor-main">
                <h3>{processor.name}</h3>
                <p>{processor.description}</p>
                <div className="processor-meta">
                  <span className="scope-badges">
                    {processor.scope.map((s) => (
                      <span key={s} className="scope-badge">
                        {s}
                      </span>
                    ))}
                  </span>
                  {processor.apiEndpoint && (
                    <span className="api-endpoint">
                      API: {processor.apiEndpoint}
                    </span>
                  )}
                </div>
              </div>
              <div className="processor-status">
                <div
                  className={`status-indicator ${
                    processor.published ? "published" : "draft"
                  }`}
                >
                  {processor.published ? (
                    <>
                      <Globe size={16} />
                      Published
                    </>
                  ) : (
                    <>
                      <Lock size={16} />
                      Draft
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="processor-actions">
              <button
                className="icon-btn"
                onClick={() => handlePublishToggle(processor)}
                title={processor.published ? "Unpublish" : "Publish"}
              >
                {processor.published ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              <button
                className="icon-btn"
                onClick={() => handleEdit(processor)}
                title="Edit"
              >
                <Edit size={16} />
              </button>
              <button
                className="icon-btn delete"
                onClick={() => handleDelete(processor)}
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="modal">
          <div className="modal-content large">
            <div className="modal-header">
              <h2>
                {editingProcessor ? "Edit Processor" : "Add New Processor"}
              </h2>
              <button
                className="modal-close"
                onClick={resetForm}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="processor-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="processorId">Processor ID *</label>
                  <input
                    type="text"
                    id="processorId"
                    value={formData.id}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, id: e.target.value }))
                    }
                    className="form-input"
                    required
                    disabled={!!editingProcessor}
                    placeholder="unique-processor-id"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="processorName">Name *</label>
                  <input
                    type="text"
                    id="processorName"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="form-input"
                    required
                    placeholder="Processor Display Name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description *</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className="form-input"
                  required
                  rows="3"
                  placeholder="Describe what this processor does..."
                />
              </div>

              <div className="form-group">
                <label>Scope *</label>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.scope.includes("file")}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData((prev) => ({
                            ...prev,
                            scope: [
                              ...prev.scope.filter((s) => s !== "file"),
                              "file",
                            ],
                          }));
                        } else {
                          setFormData((prev) => ({
                            ...prev,
                            scope: prev.scope.filter((s) => s !== "file"),
                          }));
                        }
                      }}
                    />
                    File-level processing
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.scope.includes("repository")}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData((prev) => ({
                            ...prev,
                            scope: [
                              ...prev.scope.filter((s) => s !== "repository"),
                              "repository",
                            ],
                          }));
                        } else {
                          setFormData((prev) => ({
                            ...prev,
                            scope: prev.scope.filter((s) => s !== "repository"),
                          }));
                        }
                      }}
                    />
                    Repository-level processing
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="apiEndpoint">API Endpoint</label>
                <input
                  type="url"
                  id="apiEndpoint"
                  value={formData.apiEndpoint}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      apiEndpoint: e.target.value,
                    }))
                  }
                  className="form-input"
                  placeholder="https://api.example.com/process"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="method">HTTP Method</label>
                  <select
                    id="method"
                    value={formData.apiConfiguration.method}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        apiConfiguration: {
                          ...prev.apiConfiguration,
                          method: e.target.value,
                        },
                      }))
                    }
                    className="form-input"
                  >
                    <option value="POST">POST</option>
                    <option value="GET">GET</option>
                    <option value="PUT">PUT</option>
                    <option value="PATCH">PATCH</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="timeout">Timeout (ms)</label>
                  <input
                    type="number"
                    id="timeout"
                    value={formData.apiConfiguration.timeout}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        apiConfiguration: {
                          ...prev.apiConfiguration,
                          timeout: parseInt(e.target.value),
                        },
                      }))
                    }
                    className="form-input"
                    min="1000"
                    max="300000"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>HTTP Headers</label>
                <div className="headers-section">
                  {Object.entries(formData.apiConfiguration.headers || {}).map(
                    ([key, value]) => (
                      <div key={key} className="header-row">
                        <input
                          type="text"
                          value={key}
                          onChange={(e) => {
                            const newHeaders = {
                              ...formData.apiConfiguration.headers,
                            };
                            delete newHeaders[key];
                            newHeaders[e.target.value] = value;
                            setFormData((prev) => ({
                              ...prev,
                              apiConfiguration: {
                                ...prev.apiConfiguration,
                                headers: newHeaders,
                              },
                            }));
                          }}
                          placeholder="Header name"
                          className="form-input small"
                        />
                        <input
                          type="text"
                          value={value}
                          onChange={(e) =>
                            handleHeaderChange(key, e.target.value)
                          }
                          placeholder="Header value"
                          className="form-input"
                        />
                        <button
                          type="button"
                          onClick={() => removeHeader(key)}
                          className="icon-btn delete small"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )
                  )}
                  <button
                    type="button"
                    onClick={addHeader}
                    className="btn-secondary small"
                  >
                    <Plus size={16} />
                    Add Header
                  </button>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  <Save size={16} />
                  {editingProcessor ? "Update Processor" : "Create Processor"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessorManager;
